/** Project, frame, and connection API client. */

import { apiRequest } from "./client";
import { docReaderHeaders } from "./client-protocol";
import type { Frame, FrameStatus, FrameType } from "./frame-model";

export type { Frame, FrameStatus, FrameType } from "./frame-model";

export interface Project {
  id: string;
  name: string;
  description: string;
  status: "active" | "archived";
  color: string;
  is_pinned: boolean;
  sort_order: number;
  ticket_prefix?: string;
  is_home?: boolean;
  parallel_sessions_enabled: boolean;
  /** This project's Claude login on the user's computer; no key is the default login. */
  cli_logins?: ProjectCliLogins;
  canvas_state?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface ProjectCliLogins {
  claude_cli?: string;
}

/** @deprecated In-memory adapter for pre-project-only UI contracts. */
export interface Space {
  id: string;
  project: string;
  name: string;
  ticket_prefix: string;
  canvas_state: Record<string, unknown>;
  color: string;
  is_pinned: boolean;
  is_onboarding: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface FrameReplyRoute {
  conversation_id: string | null;
  agent_key: string | null;
  task_id: string | null;
  context_kind: "manual" | "task" | "system_wake" | null;
  native_child?: boolean;
  source:
    | "frame_metadata"
    | "origin_execution"
    | "native_child_deliverable"
    | "linked_task"
    | "unbound";
}

export interface Connection {
  id: string;
  project?: string;
  /** @deprecated Compatibility field returned for older clients. */
  space?: string;
  source_frame: string;
  target_frame: string;
  label: string;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

interface ApiResponse<T> {
  status: "success";
  data: T;
}

export async function listProjects(): Promise<Project[]> {
  const res = await apiRequest<ApiResponse<Project[]>>("/api/v1/projects/");
  return res.data;
}

export async function getProject(projectId: string): Promise<Project> {
  const res = await apiRequest<ApiResponse<Project>>(
    `/api/v1/projects/${projectId}/`,
  );
  return res.data;
}

export async function createProject(data: {
  name: string;
  description?: string;
}): Promise<Project> {
  const res = await apiRequest<ApiResponse<Project>>("/api/v1/projects/", {
    method: "POST",
    body: data,
  });
  return res.data;
}

export async function updateProject(
  projectId: string,
  data: Partial<{
    name: string;
    description: string;
    color: string;
    is_pinned: boolean;
    sort_order: number;
    parallel_sessions_enabled: boolean;
    cli_logins: ProjectCliLogins;
  }>,
): Promise<Project> {
  const res = await apiRequest<ApiResponse<Project>>(
    `/api/v1/projects/${projectId}/`,
    { method: "PATCH", body: data },
  );
  return res.data;
}

export async function archiveProject(projectId: string): Promise<void> {
  await apiRequest(`/api/v1/projects/${projectId}/`, { method: "DELETE" });
}

/** Bring an archived project back; its schedules stay paused. */
export async function restoreProject(projectId: string): Promise<Project> {
  const res = await apiRequest<ApiResponse<Project>>(
    `/api/v1/projects/${projectId}/restore/`,
    { method: "POST" },
  );
  return res.data;
}

/* ---------- Project Directories ---------- */

export interface ProjectDirectory {
  id: string;
  path: string;
  label: string;
  is_repo: boolean;
  repo_url: string;
  default_base_branch: string;
  branch_prefix: string;
  pr_target_branch: string;
  branch_name_template: string;
  dev_server_prepare: string;
  dev_server_command: string;
  dev_server_url: string;
  dev_server_ready_check: string;
  dev_server_confirmed_at: string | null;
  created_at: string;
  updated_at: string;
}

export async function listProjectDirectories(
  projectId: string,
): Promise<ProjectDirectory[]> {
  const res = await apiRequest<ApiResponse<ProjectDirectory[]>>(
    `/api/v1/projects/${projectId}/directories/`,
  );
  return res.data;
}

export async function addProjectDirectory(
  projectId: string,
  data: {
    path: string;
    label?: string;
    is_repo?: boolean;
    repo_url?: string;
    default_base_branch?: string;
    branch_prefix?: string;
    pr_target_branch?: string;
    branch_name_template?: string;
    dev_server_prepare?: string;
    dev_server_command?: string;
    dev_server_url?: string;
    dev_server_ready_check?: string;
  },
): Promise<ProjectDirectory> {
  const res = await apiRequest<ApiResponse<ProjectDirectory>>(
    `/api/v1/projects/${projectId}/directories/`,
    { method: "POST", body: data },
  );
  return res.data;
}

export async function updateProjectDirectory(
  projectId: string,
  directoryId: string,
  patch: Partial<ProjectDirectory>,
): Promise<ProjectDirectory> {
  const res = await apiRequest<ApiResponse<ProjectDirectory>>(
    `/api/v1/projects/${projectId}/directories/${directoryId}/`,
    { method: "PATCH", body: patch },
  );
  return res.data;
}

export async function removeProjectDirectory(
  projectId: string,
  directoryId: string,
): Promise<void> {
  await apiRequest(
    `/api/v1/projects/${projectId}/directories/${directoryId}/`,
    { method: "DELETE" },
  );
}

export async function confirmDevServer(
  projectId: string,
  directoryId: string,
): Promise<ProjectDirectory> {
  await apiRequest(
    `/api/v1/projects/${projectId}/directories/${directoryId}/confirm-dev-server/`,
    { method: "POST", body: {} },
  );
  // The confirmation endpoint has no directory payload. Re-read the server's
  // timestamp; a client clock must never invent a successful confirmation.
  const directory = (await listProjectDirectories(projectId)).find(
    (item) => item.id === directoryId,
  );
  if (!directory) throw new Error("Folder no longer available");
  return directory;
}

/* ---------- Project-scoped frames ---------- */

interface CanvasListOptions {
  signal?: AbortSignal;
  silent?: boolean;
  /** Only these types (`?frame_types=a,b`); every type when omitted. */
  frameTypes?: readonly FrameType[];
}

export async function listFrames(projectId: string): Promise<Frame[]>;
export async function listFrames(
  projectId: string,
  options: CanvasListOptions,
): Promise<Frame[]>;
/** @deprecated The second argument is ignored. */
export async function listFrames(
  projectId: string,
  legacySpaceId: string,
): Promise<Frame[]>;
export async function listFrames(
  projectId: string,
  optionsOrLegacySpaceId?: string | CanvasListOptions,
): Promise<Frame[]> {
  const options =
    typeof optionsOrLegacySpaceId === "string"
      ? undefined
      : optionsOrLegacySpaceId;
  const path = `/api/v1/projects/${projectId}/frames/`;
  if (!options) return (await apiRequest<ApiResponse<Frame[]>>(path)).data;
  const { frameTypes, ...request } = options;
  const query = frameTypes?.length
    ? `?${new URLSearchParams({ frame_types: frameTypes.join(",") })}`
    : "";
  return (await apiRequest<ApiResponse<Frame[]>>(`${path}${query}`, request))
    .data;
}

export interface CreateFrameData {
  frame_type: FrameType;
  title?: string;
  content?: Record<string, unknown>;
  status?: FrameStatus;
  position_x?: number;
  position_y?: number;
  width?: number;
  height?: number;
  agent_id?: string | null;
  parent_frame?: string | null;
  metadata?: Record<string, unknown>;
}

export async function createFrame(
  projectId: string,
  data: CreateFrameData,
): Promise<Frame>;
/** @deprecated Pass frame data as the second argument. */
export async function createFrame(
  projectId: string,
  legacySpaceId: string,
  data: CreateFrameData,
): Promise<Frame>;
export async function createFrame(
  projectId: string,
  dataOrLegacySpaceId: CreateFrameData | string,
  legacyData?: CreateFrameData,
): Promise<Frame> {
  const data =
    typeof dataOrLegacySpaceId === "string" ? legacyData! : dataOrLegacySpaceId;
  const res = await apiRequest<ApiResponse<Frame>>(
    `/api/v1/projects/${projectId}/frames/`,
    { method: "POST", body: data, headers: docReaderHeaders() },
  );
  return res.data;
}

export type UpdateFrameData = Partial<{
  /** Explicitly rebind a conversation frame to an owned manual chat. */
  conversation_id: string;
  title: string;
  content: Record<string, unknown>;
  status: FrameStatus;
  position_x: number;
  position_y: number;
  width: number;
  height: number;
  metadata: Record<string, unknown>;
  /** The pin set over the stored metadata under the row lock; never with `metadata`. */
  metadata_patch: { pinned: boolean };
  project: string;
  /** @deprecated Moving frames between Spaces is no longer supported. */
  space: string;
}>;

export async function updateFrame(
  projectId: string,
  frameId: string,
  data: UpdateFrameData,
): Promise<Frame>;
/** @deprecated Omit the legacy Space id. */
export async function updateFrame(
  projectId: string,
  legacySpaceId: string,
  frameId: string,
  data: UpdateFrameData,
): Promise<Frame>;
export async function updateFrame(
  projectId: string,
  frameOrLegacySpaceId: string,
  dataOrFrameId: UpdateFrameData | string,
  legacyData?: UpdateFrameData,
): Promise<Frame> {
  const frameId =
    typeof dataOrFrameId === "string" ? dataOrFrameId : frameOrLegacySpaceId;
  const data = typeof dataOrFrameId === "string" ? legacyData! : dataOrFrameId;
  const res = await apiRequest<ApiResponse<Frame>>(
    `/api/v1/projects/${projectId}/frames/${frameId}/`,
    { method: "PATCH", body: data, silent: true, headers: docReaderHeaders() },
  );
  return res.data;
}

export async function deleteFrame(
  projectId: string,
  frameId: string,
): Promise<void>;
/** @deprecated Omit the legacy Space id. */
export async function deleteFrame(
  projectId: string,
  legacySpaceId: string,
  frameId: string,
): Promise<void>;
export async function deleteFrame(
  projectId: string,
  frameOrLegacySpaceId: string,
  legacyFrameId?: string,
): Promise<void> {
  const frameId = legacyFrameId ?? frameOrLegacySpaceId;
  await apiRequest(`/api/v1/projects/${projectId}/frames/${frameId}/`, {
    method: "DELETE",
    silent: true,
  });
}

export async function getFrameReplyRoute(
  projectId: string,
  frameId: string,
): Promise<FrameReplyRoute>;
/** @deprecated Omit the legacy Space id. */
export async function getFrameReplyRoute(
  projectId: string,
  legacySpaceId: string,
  frameId: string,
): Promise<FrameReplyRoute>;
export async function getFrameReplyRoute(
  projectId: string,
  frameOrLegacySpaceId: string,
  legacyFrameId?: string,
): Promise<FrameReplyRoute> {
  const frameId = legacyFrameId ?? frameOrLegacySpaceId;
  const res = await apiRequest<ApiResponse<FrameReplyRoute>>(
    `/api/v1/projects/${projectId}/frames/${frameId}/reply-route/`,
    { silent: true },
  );
  return res.data;
}

/* ---------- Connections ---------- */

export async function listConnections(projectId: string): Promise<Connection[]>;
export async function listConnections(
  projectId: string,
  options: CanvasListOptions,
): Promise<Connection[]>;
/** @deprecated The second argument is ignored. */
export async function listConnections(
  projectId: string,
  legacySpaceId: string,
): Promise<Connection[]>;
export async function listConnections(
  projectId: string,
  optionsOrLegacySpaceId?: string | CanvasListOptions,
): Promise<Connection[]> {
  const options =
    typeof optionsOrLegacySpaceId === "string"
      ? undefined
      : optionsOrLegacySpaceId;
  const path = `/api/v1/projects/${projectId}/connections/`;
  const res = options
    ? await apiRequest<ApiResponse<Connection[]>>(path, options)
    : await apiRequest<ApiResponse<Connection[]>>(path);
  return res.data;
}

/* ---------- Conversations / Messages ---------- */

export interface SpaceMessage {
  execution?: string | null;
  id: string;
  conversation: string | null;
  role: "user" | "assistant" | "system";
  content: string | Record<string, unknown> | unknown[];
  agent_id: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
}

export async function listProjectMessages(
  projectId: string,
  limit = 100,
): Promise<SpaceMessage[]> {
  const params = new URLSearchParams({ limit: String(limit) });
  const res = await apiRequest<ApiResponse<SpaceMessage[]>>(
    `/api/v1/projects/${projectId}/messages/?${params.toString()}`,
  );
  return res.data;
}

/** @deprecated Use listProjectMessages. */
export async function listSpaceMessages(
  projectId: string,
  _legacySpaceId: string,
  limit = 100,
): Promise<SpaceMessage[]> {
  return listProjectMessages(projectId, limit);
}

export async function getMessage(messageId: string): Promise<SpaceMessage> {
  const res = await apiRequest<ApiResponse<SpaceMessage>>(
    `/api/v1/messages/${messageId}/`,
  );
  return res.data;
}
